/**
 * Capture a `claude setup-token` OAuth token (sk-ant-oat01-…) non-interactively.
 *
 * Shared by the GitHub Actions setup (setupGithub.ts) and the CLOUD installer
 * path (install.ts). It is CLOUD-ONLY for `synkro install`: local installs use
 * the macOS keychain bridge (macKeychain.ts) and never call this.
 *
 * `claude setup-token` renders its output as a TUI, so we run it under `script`
 * to capture the pty output, then scrape the token (Claude renders it in the
 * RGB 255,193,7 yellow). The user approves the OAuth in a browser; we never log
 * the token and delete the capture file immediately.
 */
import { spawn as nodeSpawn } from 'node:child_process';
import { readFileSync, unlinkSync } from 'node:fs';
import { homedir, platform } from 'node:os';
import { join } from 'node:path';

const SYNKRO_DIR = join(homedir(), '.synkro');

export function captureClaudeSetupToken(): Promise<string> {
  const tmpFile = join(SYNKRO_DIR, `token-capture-${Date.now()}.raw`);
  // `script`'s arg order differs by platform:
  //   BSD/macOS:   script -q <file> <cmd> [args...]
  //   util-linux:  script -qec '<cmd>' <file>
  const isMac = platform() === 'darwin';
  const bin = 'script';
  const args = isMac
    ? ['-q', tmpFile, 'claude', 'setup-token']
    : ['-qec', 'claude setup-token', tmpFile];
  // When claude.ai rejects the browser approval, `claude setup-token` ends
  // without ever printing a token (the user has no code to paste back, so they
  // Ctrl-C and we exit non-zero, or it exits 0 with nothing). The most common
  // cause is NOT a Synkro bug: a request-rewriting browser extension (Grammarly,
  // ad/script blockers, AI-assistant toolbars) strips the OAuth form's
  // Content-Type — claude.ai then shows "Authorization failed — Unsupported
  // media type" — or the user approved on the wrong Claude account. Surface that
  // instead of a bare exit code / empty-capture error so the customer can self-fix.
  const OAUTH_HINT =
    'The browser approval did not return a token. This usually means claude.ai rejected ' +
    'the OAuth request (its "Authorization failed — Unsupported media type" page), most often ' +
    'because a browser extension (Grammarly, ad/script blockers, AI-assistant toolbars) stripped ' +
    'the request headers, or you approved on the wrong Claude account. Fix: retry in a clean ' +
    'incognito window with extensions disabled, and approve on the Claude account you want the ' +
    'cloud workers to use.';
  return new Promise((resolve, reject) => {
    // Force deterministic 24-bit color so the token's yellow is ALWAYS emitted as
    // 38;2;255;193;7 — not a 256-color/basic approximation that varies by the
    // user's terminal. The extractor keys on that exact foreground color.
    const proc = nodeSpawn(bin, args, {
      stdio: 'inherit',
      env: { ...process.env, FORCE_COLOR: '3', COLORTERM: 'truecolor', TERM: 'xterm-256color' },
    });
    proc.on('error', (err) => reject(new Error(`Failed to spawn claude setup-token: ${err.message}`)));
    proc.on('close', (code) => {
      let raw = '';
      try { raw = readFileSync(tmpFile, 'utf-8'); } catch (e) {
        reject(new Error(`Could not read script output file: ${(e as Error).message}`));
        return;
      }
      try { unlinkSync(tmpFile); } catch {}
      if (code !== 0) { reject(new Error(`claude setup-token exited with code ${code}. ${OAUTH_HINT}`)); return; }
      // The token — and ONLY the token — is rendered in yellow (24-bit fg
      // 255,193,7); every other character on screen is a different color. So we do
      // NOT parse the token's shape at all: no `sk-ant-oat01` match, no
      // `[A-Za-z0-9_-]` class, no length check. Every such regex matches forward
      // until the first byte it doesn't expect and then STOPS — and the terminal
      // injects stray control bytes mid-token at each line-wrap, so any shape regex
      // truncates there (that bug stored 90 of 108 chars and 401'd every grade).
      //
      // Instead: a tiny ANSI SGR foreground-color state machine. Walk the output,
      // track the current fg color, and collect EVERY printable character emitted
      // while fg is the token-yellow — across as many wraps/spans as it takes.
      // Whitespace/control bytes the wrap injects are simply not collected; ANSI
      // escapes are consumed to update color, never emitted. What remains is the
      // entire yellow run = the whole token, however long it is.
      const TOKEN_YELLOW = '2;255;193;7';
      let fg = 'default';
      let token = '';
      let i = 0;
      while (i < raw.length) {
        if (raw[i] === '\x1B') {
          // SGR ("…m"): update the foreground-color state.
          const sgr = /^\x1B\[([0-9;?]*)m/.exec(raw.slice(i));
          if (sgr) {
            const codes = sgr[1].split(';').map((n) => Number(n) || 0);
            for (let k = 0; k < codes.length; k++) {
              const c = codes[k];
              if (c === 0 || c === 39) fg = 'default';
              else if (c === 38 && codes[k + 1] === 2) { fg = `2;${codes[k + 2]};${codes[k + 3]};${codes[k + 4]}`; k += 4; }
              else if (c === 38 && codes[k + 1] === 5) { fg = `5;${codes[k + 2]}`; k += 2; }
              else if ((c >= 30 && c <= 37) || (c >= 90 && c <= 97)) fg = `basic;${c}`;
              // other codes (bold, bg, underline, …) don't change the fg color
            }
            i += sgr[0].length;
            continue;
          }
          // Any other CSI or OSC escape: consume it without touching color.
          const csi = /^\x1B\[[0-9;?]*[ -/]*[@-~]/.exec(raw.slice(i));
          const osc = /^\x1B\][^\x07]*(?:\x07|\x1B\\)/.exec(raw.slice(i));
          if (csi) { i += csi[0].length; continue; }
          if (osc) { i += osc[0].length; continue; }
          i += 1;
          continue;
        }
        // A printable byte: collect it iff fg is the token-yellow and it isn't a
        // whitespace/control byte (the wrap injects spaces/newlines into the run).
        const ch = raw[i];
        if (fg === TOKEN_YELLOW && !/[\s\x00-\x1F\x7F]/.test(ch)) token += ch;
        i += 1;
      }
      // The ONLY guard — not a length/shape check, just "did we see any yellow at
      // all". An empty capture means color wasn't emitted, not a short token.
      if (!token) {
        // Two distinct causes share this branch. A substantial capture with no
        // yellow run = the terminal didn't emit color. A near-empty capture =
        // setup-token finished without ever rendering a token, i.e. the OAuth
        // approval never came back (same root cause as the non-zero-exit path).
        const reason = raw.length < 200
          ? OAUTH_HINT
          : `is the terminal emitting color? (captured ${raw.length}b but no token-yellow run)`;
        reject(new Error(`Captured no setup token from claude setup-token output. ${reason}`));
        return;
      }
      resolve(token);
    });
  });
}
